import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../Contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export const useAutoLogout = (timeoutMs: number = 7200000) => { // 2 horas por padrão
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      
      if (timeSinceLastActivity >= timeoutMs) {
        logout();
        navigate('/admin/login');
        alert('Sessão expirada por inatividade. Faça login novamente.');
      }
    }, timeoutMs);
  }, [timeoutMs, logout, navigate]);

  const updateActivity = useCallback(() => {
    if (user) {
      resetTimer();
    }
  }, [user, resetTimer]);

  useEffect(() => {
    if (!user) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    // Eventos para detectar atividade do usuário
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus',
      'input'
    ];

    // Adicionar listeners para todos os eventos
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Iniciar o timer
    resetTimer();

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [user, updateActivity, resetTimer]);

  // Retornar função para reset manual do timer (útil para ações específicas)
  const manualReset = useCallback(() => {
    updateActivity();
  }, [updateActivity]);

  return { manualReset };
};
